import type { ObjectId } from 'mongodb';

import type { IsoDateTimeString } from './Helpers';
import type { MilestoneTemplateType } from './Milestones';

/**
 * @deprecated This is replaced by AccountStipulationType
 */
export enum StipulationType {
  achRequirement = 'achRequirement',
  creditCheckConsent = 'creditCheckConsent',
  employmentVerification = 'employmentVerification',
  identityVerification = 'identityVerification',
  incomeVerification = 'incomeVerification',
  prequalificationConsent = 'prequalificationConsent',
  titleVerification = 'titleVerification',
  txtMessageNotificationsConsent = 'txtMessageNotificationsConsent',
  termsAndConditions = 'termsAndConditions',
}

/**
 * @deprecated This is replaced by AccountMilestoneStipulationDocument
 */
export type Stipulation = {
  stipulationType: StipulationType;
  description: string;
  isSatisfied?: boolean;
  requiresReview?: boolean;
};

export enum AccountStipulationType {
  administrative = 'administrative',
  designVerification = 'designVerification',
  changeOrder = 'changeOrder',
}

export enum AccountStipulationReason {
  accountDetails = 'Account Details',
  consumerCompliance = 'Consumer Compliance',
  housingStock = 'Housing Stock',
  customerActionRequired = 'Customer Action Required',
  /** @deprecated Use {@link customerActionRequired} instead */
  ageRelated = 'Age Related',
  potentialOFACMatch = 'Potential OFAC Match',
  kycMismatch = 'KYC Mismatch',
  creditFraudAlert = 'Credit Fraud Alert',
  emailVerification = 'Email Verification',
  welcomeCall = 'Welcome Call',
  changeOrderPending = 'Change Order Pending',
  other = 'Other',
}

/** Mirrors Recheck Verification Calls API `status` field. */
export enum RecheckVerificationCallStatus {
  created = 'created',
  expired = 'expired',
  completed = 'completed',
}

/** Mirrors Recheck Verification Calls API `disposition` field. */
export enum RecheckVerificationCallDisposition {
  pending = 'pending',
  pass = 'pass',
  flag = 'flag',
  fail = 'fail',
}

/** Mirrors Recheck Verification Calls API signal `classification` field. */
export enum RecheckVerificationCallSignalClassification {
  pass = 'pass',
  flag = 'flag',
}

export type RecheckVerificationCallSignal = {
  name: string;
  score: number;
  classification: RecheckVerificationCallSignalClassification;
};

/** Recheck's `review` block, present on `verification.call.reviewed` deliveries. */
export type RecheckVerificationCallReview = {
  reviewed_at?: IsoDateTimeString;
  previous_disposition?: RecheckVerificationCallDisposition;
  note?: string;
};

/** Recheck verification-call state persisted on a Welcome Call stipulation. */
export type RecheckVerificationCall = {
  callId: string; // "call_<uuid>"
  url?: string; // hosted call url from POST /calls/ 201
  createdDate?: IsoDateTimeString; // from 201
  expiresAt?: IsoDateTimeString | null;
  status?: RecheckVerificationCallStatus;
  disposition?: RecheckVerificationCallDisposition;
  signals?: RecheckVerificationCallSignal[];
  dashboardUrl?: string;
  completedAt?: IsoDateTimeString | null;
  review?: RecheckVerificationCallReview;
};

/**
 * Shape of Recheck's GET /calls/{id}/ response, which is also the `payload`
 * object carried by the inbound `verification.call.completed` and
 * `verification.call.reviewed` webhooks (see {@link RecheckVerificationCallWebhookEvent}).
 * Single source of truth shared by the call client and the webhook handler.
 */
export type RecheckVerificationCallCompletedPayload = {
  id: string; // call_<uuid>
  status: RecheckVerificationCallStatus;
  disposition: RecheckVerificationCallDisposition;
  signals: RecheckVerificationCallSignal[];
  dashboard_url: string;
  completed_at: IsoDateTimeString | null;
  expires_at?: IsoDateTimeString | null;
  reference_id?: string;
  review?: RecheckVerificationCallReview;
};

/** The inbound webhook event name Recheck sends when a call finishes. */
export const RECHECK_VERIFICATION_CALL_COMPLETED_EVENT = 'verification.call.completed';

/** The inbound webhook event name Recheck sends after a reviewer decides a flagged call. */
export const RECHECK_VERIFICATION_CALL_REVIEWED_EVENT = 'verification.call.reviewed';

/**
 * Envelope of an inbound Recheck webhook delivery. The call object lives under
 * `payload`; the top-level `id` is the event delivery id (NOT the call id), and
 * `event` distinguishes delivery types (e.g. `verification.call.completed`).
 */
export type RecheckVerificationCallWebhookEvent = {
  event: string;
  id: string; // event delivery id (e.g. 228022ff-...), not the call_<uuid>
  created_date: IsoDateTimeString;
  payload: RecheckVerificationCallCompletedPayload;
};

/** The inbound webhook event name Recheck sends when two individuals are merged into one. */
export const RECHECK_INDIVIDUAL_MERGE_EVENT = 'network.individual.merge';

/** Payload of the `network.individual.merge` webhook: the merged-away individual and the survivor. */
export type RecheckIndividualMergePayload = {
  from_recheck_id: string;
  to_recheck_id: string;
};

export type RecheckIndividualMergeWebhookEvent = {
  event: string;
  id: string; // event delivery id, not a Recheck individual id
  created_date: IsoDateTimeString;
  payload: RecheckIndividualMergePayload;
};

export type AccountMilestoneStipulationDocument = {
  id: ObjectId;
  accountId: ObjectId;
  accountMilestoneType: MilestoneTemplateType;
  accountMilestoneId: ObjectId;
  appliedBy: string;
  appliedByDisplayName: string;
  appliedDate: Date;
  stipulationType: AccountStipulationType;
  description: string;
  isSatisfied: boolean;
  isSatisfiedNote: string;
  reason?: AccountStipulationReason;
  requiresReview: boolean;
  isAppliedByAutomation?: boolean;
  fraudAlertReminderSentAt?: Date;
  fraudAlertCancellationProcessedAt?: Date;
  recheckVerificationCall?: RecheckVerificationCall;
};

export type AccountMilestoneStipulation = {
  customerName: string;
  milestoneType: string;
  customerAddress: string;
  customerAccountLink: string;
};
